import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, Pencil, Trash2, Tag, AlertCircle } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { kanbanApiService } from '../../services/kanbanApi';
import type { KanbanLabel } from '../../types/kanban';
import { cn } from '../../utils/cn';
import { ConfirmationModal } from '../ConfirmationModal';

interface KanbanLabelManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  boardId: string;
  labels: KanbanLabel[];
  onUpdated: () => void;
}

export const KanbanLabelManagementModal = ({
  isOpen,
  onClose,
  boardId,
  labels,
  onUpdated,
}: KanbanLabelManagementModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  /** Confirmation is portaled to `document.body`; ignore outside clicks while it is open. */
  const deleteTargetRef = useRef<KanbanLabel | null>(null);

  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#6366f1');
  const [newDescription, setNewDescription] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<KanbanLabel | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  deleteTargetRef.current = deleteTarget;
  const handleClickOutside = useCallback(() => {
    if (deleteTargetRef.current) return;
    onClose();
  }, [onClose]);
  useOnClickOutside(modalRef, handleClickOutside);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setNewName('');
    setNewColor('#6366f1');
    setNewDescription('');
    setEditingId(null);
    setDeleteTarget(null);
  }, [isOpen]);

  const startEdit = (label: KanbanLabel) => {
    setEditingId(label.id);
    setEditName(label.name);
    setEditColor(label.color);
    setEditDescription(label.description ?? '');
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) {
      setError('Label name is required.');
      return;
    }
    setIsCreating(true);
    setError(null);
    try {
      await kanbanApiService.createLabel(boardId, {
        name,
        color: newColor.trim() || '#6366f1',
        description: newDescription.trim() || undefined,
      });
      setNewName('');
      setNewColor('#6366f1');
      setNewDescription('');
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create label.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSaveEdit = async (labelId: string) => {
    const name = editName.trim();
    if (!name) {
      setError('Label name is required.');
      return;
    }
    setSavingId(labelId);
    setError(null);
    try {
      // Fetch fresh label data before update to verify it still exists
      await kanbanApiService.getLabel(labelId);
      await kanbanApiService.updateLabel(labelId, {
        name,
        color: editColor.trim() || '#6366f1',
        description: editDescription.trim() || null,
      });
      setEditingId(null);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update label.');
    } finally {
      setSavingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setError(null);
    try {
      await kanbanApiService.deleteLabel(deleteTarget.id);
      setDeleteTarget(null);
      if (editingId === deleteTarget.id) setEditingId(null);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete label.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999] overflow-y-auto">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          type="button"
          onClick={onClose}
          className="modal-close z-10"
          aria-label="Close"
          disabled={isCreating || !!savingId || isDeleting}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="modal-header">
          <h2 className="modal-title flex items-center gap-2">
            <Tag className="w-5 h-5 text-accent-400 shrink-0" />
            Board labels
          </h2>
        </div>

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleCreate} className="space-y-3 pb-4 border-b border-glass-200/10 mb-4">
          <p className="text-sm font-semibold text-white">Add label</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="new-label-name" className="text-xs font-medium text-glass-400 mb-1.5 block">
                Name
              </label>
              <input
                id="new-label-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="glass-input w-full"
                disabled={isCreating}
              />
            </div>
            <div>
              <label htmlFor="new-label-color" className="text-xs font-medium text-glass-400 mb-1.5 block">
                Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="new-label-color"
                  type="color"
                  value={newColor.startsWith('#') && newColor.length >= 7 ? newColor.slice(0, 7) : '#6366f1'}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="h-9 w-12 cursor-pointer rounded border border-glass-200/20 bg-transparent p-0.5"
                  disabled={isCreating}
                  aria-label="Label color"
                />
                <input
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="glass-input flex-1 font-mono"
                  disabled={isCreating}
                />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="new-label-desc" className="text-xs font-medium text-glass-400 mb-1.5 block">
                Description
              </label>
              <input
                id="new-label-desc"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                className="glass-input w-full"
                disabled={isCreating}
              />
            </div>
          </div>
          <button
            type="submit"
            className={cn('btn-primary', isCreating && 'opacity-70')}
            disabled={isCreating}
          >
            {isCreating ? <Loader className="w-4 h-4 animate-spin" /> : null}
            Add label
          </button>
        </form>

        <div className="overflow-y-auto flex-1 space-y-2 min-h-0 pr-1">
          {labels.length === 0 ? (
            <p className="text-sm text-glass-500">No labels yet.</p>
          ) : (
            labels.map((label) => {
              const isEditing = editingId === label.id;
              return (
                <div
                  key={label.id}
                  className="subcard p-3 flex flex-col gap-2"
                >
                  {isEditing ? (
                    <>
                      <div className="flex items-start gap-3">
                        <input
                          type="color"
                          value={
                            editColor.startsWith('#') && editColor.length >= 7
                              ? editColor.slice(0, 7)
                              : '#6366f1'
                          }
                          onChange={(e) => setEditColor(e.target.value)}
                          className="h-9 w-12 shrink-0 cursor-pointer rounded border border-glass-200/20 bg-transparent p-0.5 mt-5"
                          disabled={savingId === label.id}
                          aria-label="Label color"
                        />
                        <div className="flex-1 space-y-2">
                          <input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="glass-input w-full"
                            disabled={savingId === label.id}
                          />
                          <input
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="glass-input w-full"
                            placeholder="Description"
                            disabled={savingId === label.id}
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="glass-button-secondary"
                          onClick={cancelEdit}
                          disabled={savingId === label.id}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="glass-button"
                          onClick={() => handleSaveEdit(label.id)}
                          disabled={savingId === label.id}
                        >
                          {savingId === label.id ? <Loader className="w-4 h-4 animate-spin" /> : null}
                          Save
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-start gap-3">
                      <span
                        className="w-3 h-3 rounded-full shrink-0 mt-1.5 ring-1 ring-white/20"
                        style={{ backgroundColor: label.color }}
                        title={label.color}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">{label.name}</p>
                        {label.description ? (
                          <p className="text-sm text-glass-400 mt-0.5">{label.description}</p>
                        ) : null}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          type="button"
                          className="btn-icon btn-icon-sm"
                          title="Edit label"
                          aria-label="Edit label"
                          onClick={() => startEdit(label)}
                          disabled={!!savingId || isCreating || isDeleting}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                          title="Delete label"
                          aria-label="Delete label"
                          onClick={() => setDeleteTarget(label)}
                          disabled={!!savingId || isCreating || isDeleting}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="glass-button-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {deleteTarget ? (
        <ConfirmationModal
          isOpen
          onClose={() => !isDeleting && setDeleteTarget(null)}
          onConfirm={confirmDelete}
          title="Delete label"
          message={`Remove “${deleteTarget.name}” from this board? This cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          isLoading={isDeleting}
        />
      ) : null}
    </div>
  );

  return createPortal(modalContent, document.body);
};
